"use client";

import { useState, type ReactNode } from "react";
import { Info, MapPin } from "lucide-react";
import {
  RECIPE_PLACEHOLDER_IMAGE,
  fallbackToRecipePlaceholder,
} from "@/lib/recipePlaceholder";

export interface Plant {
  id: string;
  commonName: string;
  scientificName: string;
  imageUrl?: string;
  sunLevel: string;
  category: string;
  description?: string;
  waterNeeds?: string;
  zones?: string[];
  careInstructions?: string;
  height?: string;
  width?: string;
  bloomTime?: string;
  bloomColor?: string;
  price?: number | null;
  shopifyVariantId?: string | null;
}

/**
 * Most plants a customer can select for one generation (PLA2-762). The image
 * model's guidance is 14 images total and we also send the garden photo and
 * the recipe inspiration image, so 14 − 2 = 12. Recipes can still carry more
 * plants (more resilient to sold-out inventory) — this caps the selection only.
 */
export const MAX_SELECTED_PLANTS = 12;

export interface RecipeInfo {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  style: string;
  sunLevel: string;
  usableZones?: number[];
}

interface PlantListProps {
  plants: Plant[];
  selectedPlants: string[];
  onTogglePlant: (plantId: string) => void;
  onViewPlantDetails: (plant: Plant) => void;
  recipeInfo?: RecipeInfo;
  loading?: boolean;
  /**
   * Ids of plants that are out of stock. These cards render disabled with a
   * "Sold out" badge and cannot be selected (PLA2-458).
   */
  soldOutPlantIds?: string[];
  /** Selection cap; defaults to MAX_SELECTED_PLANTS (the container flow uses 8). */
  maxSelected?: number;
  /** Extra line per card under the name, keyed by plant id (container role + count). */
  cardExtras?: Record<string, ReactNode>;
  /**
   * Out-of-stock alternatives: substitute plant id → name of the sold-out
   * recipe plant it replaces. Shown as a "Replaces …" badge; substitutes are
   * not counted as recipe plants.
   */
  substitutedFor?: Record<string, string>;
}

export function PlantList({
  plants,
  selectedPlants,
  onTogglePlant,
  onViewPlantDetails,
  recipeInfo,
  loading = false,
  soldOutPlantIds = [],
  maxSelected = MAX_SELECTED_PLANTS,
  cardExtras,
  substitutedFor = {},
}: PlantListProps) {
  const recipeCount = plants.filter((p) => !(p.id in substitutedFor)).length;
  // Show only recipe plants without search functionality. Available plants
  // come first; sold-out ones sink to the bottom (stable, so recipe order is
  // kept within each group).
  const filteredPlants = soldOutPlantIds.length
    ? [...plants].sort(
        (a, b) =>
          Number(soldOutPlantIds.includes(a.id)) -
          Number(soldOutPlantIds.includes(b.id))
      )
    : plants;

  const selectedCount = selectedPlants.length;

  const [erroredImages, setErroredImages] = useState<Set<string>>(new Set());

  const formatSunLevel = (sunLevel: string) => {
    return sunLevel
      .replace(/_/g, " ")
      .replace("PART SUN PART SHADE", "Part Sun")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatZone = (zone: number | string) => {
    const zoneStr = zone.toString();
    return zoneStr.replace(/^ZONE_/i, "");
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Recipe card skeleton */}
        {recipeInfo && (
          <div className="bg-gray-100 dark:bg-plantista-900 rounded-xl p-6 border border-gray-200 dark:border-plantista-800">
            <div className="flex items-start space-x-4">
              <div className="w-24 h-24 bg-gray-200 dark:bg-plantista-800 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-3">
                <div className="h-6 bg-gray-200 dark:bg-plantista-800 rounded w-1/3" />
                <div className="h-4 bg-gray-200 dark:bg-plantista-800 rounded w-2/3" />
                <div className="h-4 bg-gray-200 dark:bg-plantista-800 rounded w-1/4" />
              </div>
            </div>
          </div>
        )}

        {/* Header skeleton */}
        <div className="space-y-2">
          <div className="h-8 bg-gray-200 dark:bg-plantista-800 rounded w-1/4" />
          <div className="h-4 bg-gray-200 dark:bg-plantista-800 rounded w-1/3" />
        </div>

        {/* Plant cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div
              key={i}
              className="border-2 border-gray-200 dark:border-plantista-800 rounded-lg overflow-hidden bg-white dark:bg-plantista-900"
            >
              <div className="h-32 bg-gray-200 dark:bg-plantista-800" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-plantista-800 rounded w-3/4" />
                <div className="h-3 bg-gray-200 dark:bg-plantista-800 rounded w-full" />
                <div className="space-y-1">
                  <div className="h-3 bg-gray-200 dark:bg-plantista-800 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 dark:bg-plantista-800 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 dark:bg-plantista-800 rounded w-3/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 md:px-0">
      {/* Recipe Information Card */}
      {recipeInfo && (
        <div className="bg-white dark:bg-plantista-900 rounded-xl p-4 md:p-6 border border-gray-200 dark:border-plantista-800 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {/* Recipe Image */}
            <div className="w-full sm:w-24 h-48 sm:h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-plantista-950 shadow-md">
              <img
                src={recipeInfo.imageUrl || RECIPE_PLACEHOLDER_IMAGE}
                alt={recipeInfo.name}
                className="w-full h-full object-cover"
                onError={fallbackToRecipePlaceholder}
              />
            </div>

            {/* Recipe Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className="text-xl font-bold text-plantista-950 dark:text-white font-display">
                  {recipeInfo.name}
                </h3>
              </div>
              {recipeInfo.description && (
                <p className="text-plantista-600 dark:text-plantista-300 text-sm mb-3 line-clamp-3 sm:line-clamp-2">
                  {recipeInfo.description}
                </p>
              )}
              {recipeInfo.usableZones && recipeInfo.usableZones.length > 0 && (
                <div className="flex items-center space-x-1 text-sm">
                  <MapPin className="w-4 h-4 text-yellow-500" />
                  <span className="text-plantista-600 dark:text-plantista-400">
                    {recipeInfo.usableZones.map(formatZone).join(", ")}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white font-display">
            Recipe Plants
          </h2>
          <p className="text-sm md:text-base text-plantista-600 dark:text-plantista-300">
            {plants.length >= maxSelected
              ? `Select up to ${maxSelected} plants for your design (${selectedCount} of ${maxSelected} selected${
                  recipeCount > maxSelected
                    ? ` · ${recipeCount} in recipe`
                    : ""
                })`
              : `${selectedCount} of ${plants.length} plants selected`}
          </p>
        </div>

        {/* A 14-plant recipe tops out at MAX_SELECTED_PLANTS, so "all" means
            "as many as allowed" (PLA2-762). */}
        {plants.length > 0 &&
          selectedCount === Math.min(plants.length, maxSelected) && (
            <button
              onClick={() => {
                // Deselect all
                plants.forEach((plant) => {
                  if (selectedPlants.includes(plant.id)) {
                    onTogglePlant(plant.id);
                  }
                });
              }}
              className="text-sm text-plantista-600 dark:text-plantista-400 hover:text-plantista-900 dark:hover:text-white font-medium transition"
            >
              Deselect All
            </button>
          )}
      </div>

      {/* Plant Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filteredPlants.map((plant) => {
          const isSelected = selectedPlants.includes(plant.id);
          const isSoldOut = soldOutPlantIds.includes(plant.id);
          return (
            <div
              key={plant.id}
              className={`
                relative rounded-lg border-2 transition-all overflow-hidden bg-white dark:bg-plantista-900 flex flex-col h-full
                ${isSoldOut ? "opacity-60" : ""}
                ${
                  isSelected
                    ? "border-plantista-500 dark:border-plantista-400 ring-2 ring-plantista-500 dark:ring-plantista-400"
                    : isSoldOut
                      ? "border-gray-300 dark:border-plantista-800"
                      : "border-gray-200 dark:border-plantista-800 hover:border-plantista-500 dark:hover:border-plantista-400 hover:shadow-sm"
                }
              `}
            >
              {/* Swapped in for a sold-out recipe plant */}
              {!isSoldOut && substitutedFor[plant.id] && (
                <span className="absolute top-2 right-2 z-10 max-w-[70%] truncate px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-plantista-100 text-plantista-700 dark:bg-plantista-800 dark:text-plantista-200">
                  Replaces {substitutedFor[plant.id]}
                </span>
              )}

              {/* Sold-out badge */}
              {isSoldOut && (
                <span className="absolute top-2 right-2 z-10 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
                  Sold out
                </span>
              )}

              {/* Checkbox overlay */}
              <button
                onClick={() => {
                  if (isSoldOut) return;
                  onTogglePlant(plant.id);
                }}
                disabled={isSoldOut}
                aria-disabled={isSoldOut}
                className={`absolute top-2 left-2 z-10${
                  isSoldOut ? " cursor-not-allowed" : ""
                }`}
                aria-label={
                  isSoldOut
                    ? "Plant is sold out"
                    : isSelected
                      ? "Deselect plant"
                      : "Select plant"
                }
              >
                <div
                  className={`
                    w-5 h-5 rounded border-2 flex items-center justify-center transition-all shadow-sm
                    ${
                      isSelected
                        ? "bg-plantista-500 dark:bg-plantista-400 border-plantista-500 dark:border-plantista-400"
                        : isSoldOut
                          ? "bg-gray-100 dark:bg-plantista-950 border-gray-300 dark:border-plantista-700"
                          : "bg-white dark:bg-plantista-950 border-gray-300 dark:border-plantista-600 hover:border-plantista-500 dark:hover:border-plantista-400"
                    }
                  `}
                >
                  {isSelected && (
                    <svg
                      className="w-3.5 h-3.5 text-white dark:text-plantista-950"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>

              {/* Plant Image */}
              <div className="relative h-48 bg-gray-200 dark:bg-plantista-800 shrink-0">
                {plant.imageUrl && !erroredImages.has(plant.id) ? (
                  <img
                    src={plant.imageUrl}
                    alt={plant.commonName}
                    className="w-full h-full object-cover"
                    onError={() => {
                      setErroredImages((prev) => {
                        if (prev.has(plant.id)) return prev;
                        const next = new Set(prev);
                        next.add(plant.id);
                        return next;
                      });
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-plantista-800">
                    <svg
                      className="w-12 h-12 text-gray-400 dark:text-plantista-700"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                {isSelected && (
                  <div className="absolute inset-0 bg-plantista-500/20 dark:bg-plantista-400/20" />
                )}
              </div>

              {/* Plant Info */}
              <div className="p-3 flex flex-col flex-1">
                <div className="mb-2">
                  <h3 className="font-semibold text-plantista-950 dark:text-white text-base leading-tight mb-0.5">
                    {plant.commonName}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-plantista-400 italic line-clamp-1">
                    {plant.scientificName}
                  </p>
                  {cardExtras?.[plant.id] && (
                    <div className="mt-1.5">{cardExtras[plant.id]}</div>
                  )}
                </div>

                {/* Compact Info Grid */}
                <div className="space-y-1.5 mb-3 text-xs flex-1">
                  {/* Category & Sun */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="capitalize bg-gray-100 dark:bg-plantista-800 px-2 py-0.5 rounded text-xs font-medium text-gray-700 dark:text-plantista-300">
                      {plant.category}
                    </span>
                    <span className="flex items-center gap-1 text-gray-600 dark:text-plantista-400">
                      <svg
                        className="w-3.5 h-3.5 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 00-1.668-1.973z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-xs">
                        {formatSunLevel(plant.sunLevel)}
                      </span>
                    </span>
                  </div>

                  {/* Water Needs */}
                  {plant.waterNeeds && (
                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-plantista-400">
                      <svg
                        className="w-3.5 h-3.5 text-blue-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.5 3A2.5 2.5 0 003 5.5v9A2.5 2.5 0 005.5 17h9a2.5 2.5 0 002.5-2.5v-9A2.5 2.5 0 0014.5 3h-9zm0 2h9a.5.5 0 01.5.5v.5H5v-.5a.5.5 0 01.5-.5z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-xs capitalize">
                        {plant.waterNeeds.toLowerCase()}
                      </span>
                    </div>
                  )}

                  {/* Zones */}
                  {plant.zones && plant.zones.length > 0 && (
                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-plantista-400">
                      <svg
                        className="w-3.5 h-3.5 text-green-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-xs">
                        Zones {plant.zones.join(", ")}
                      </span>
                    </div>
                  )}

                  {/* Bloom Time & Color */}
                  {(plant.bloomTime || plant.bloomColor) && (
                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-plantista-400">
                      <svg
                        className="w-3.5 h-3.5 text-pink-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
                      </svg>
                      <span className="text-xs">
                        {plant.bloomTime && (
                          <span className="capitalize">
                            {plant.bloomTime.toLowerCase()}
                          </span>
                        )}
                        {plant.bloomTime && plant.bloomColor && (
                          <span className="text-gray-400 dark:text-plantista-600">
                            {" "}
                            •{" "}
                          </span>
                        )}
                        {plant.bloomColor && (
                          <span className="capitalize">
                            {plant.bloomColor.toLowerCase()}
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Size (Height x Width) */}
                  {(plant.height || plant.width) && (
                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-plantista-400">
                      <svg
                        className="w-3.5 h-3.5 text-purple-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                      <span className="text-xs">
                        {plant.height && <span>{plant.height}</span>}
                        {plant.height && plant.width && <span> × </span>}
                        {plant.width && <span>{plant.width}</span>}
                      </span>
                    </div>
                  )}
                </div>

                {/* View Details Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewPlantDetails(plant);
                  }}
                  className="w-full mt-auto py-1.5 px-2 bg-white dark:bg-plantista-900 border border-gray-300 dark:border-plantista-700 text-gray-600 dark:text-plantista-300 rounded-md hover:bg-gray-50 dark:hover:bg-plantista-800 transition-colors flex items-center justify-center gap-1.5 font-medium text-xs"
                >
                  <Info className="w-3.5 h-3.5" />
                  <span>Details</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {plants.length === 0 && !loading && (
        <div className="text-center py-16 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-lg">
          <svg
            className="w-16 h-16 text-yellow-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="text-yellow-800 dark:text-yellow-400 text-lg font-semibold mb-2">
            No plants available for this recipe
          </p>
          <p className="text-yellow-600 dark:text-yellow-300 text-sm max-w-md mx-auto">
            This recipe hasn't been configured with plants yet. Please select a
            different recipe or contact support.
          </p>
        </div>
      )}
    </div>
  );
}
